import React, { CSSProperties, useCallback, useContext, useEffect, useRef, useState } from 'react';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import DevicesIcon from '@mui/icons-material/Devices';
import StorefrontIcon from '@mui/icons-material/Storefront';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import { QRCodeSVG } from 'qrcode.react';
import { getAccessToken } from 'utils/auth';
import { logAppsFlyerEvent, AF_EVENT } from 'services/appsflyer';
import { currencyFormatter } from 'utils/functions';
import { SOCKET_SERVER_URL } from 'utils/urls';
import { errorToLogObject, pickApiErrorMessage, safeReadJson } from 'utils/apiErrors';
import { TMXPageData } from 'utils/threatmetrix';
import {
  ADDRESS_REQUIRED_TRANSACTION_MESSAGE,
  CONNECTION_ERROR_MESSAGE,
  ERROR_MESSAGE,
  GO_TO_ACCOUNT_SETTINGS_BUTTON,
  IDENTITY_CHECK_FAILED_MESSAGE,
  IDENTITY_PROCESS_CHECKING
} from 'utils/unityStrings';
import { checkAddressRequired } from './useAddressCheck';
import { useRiskAssessment } from './useRiskAssessment';

import { CashierContext, PaymentMethod } from './CashierContext';
import { parseCurrencyAmount, sanitizeCurrencyAmount } from './currencyInput';
import { promoAppliedLabel, verifyPromoCode } from './promoVerify';
import { useCashierLimits } from './useCashierLimits';

function getDescopeToken(): string {
  return getAccessToken();
}

import { usePlayerProfile } from 'components/Profile/usePlayerProfile';

import CreditCardForm from './CreditCardForm';
import KYCModal from './KYCModal';
import TierExplainer from './TierExplainer';
import { useClubs } from './useClubs';

const QUICK_AMOUNTS = [10, 25, 50, 100, 250];

// Credit-card processing fee. The card is charged amount * (1 + rate); Cage
// deposits are fee-free (that path shows "Hijack fee $0.00"). Kept in one
// place so the Payment Information line and the Continue button can't drift.
const CREDIT_CARD_PROCESSING_FEE_RATE = 0.1;

const normalizeWalletAddress = (address?: string | null) => {
  const normalizedAddress = address?.trim() || '';
  return normalizedAddress && normalizedAddress.toUpperCase() !== 'NA' ? normalizedAddress : '';
};

const DIGITAL_METHODS: {
  key: PaymentMethod;
  label: string;
  icon: React.ReactNode;
  desc: string;
}[] = [
  {
    key: 'credit_card',
    label: 'Credit Card',
    icon: <CreditCardIcon style={{ fontSize: '1.2rem' }} />,
    desc: 'Visa, Mastercard, Amex'
  },
  {
    key: 'crypto',
    label: 'Bitcoin',
    icon: <CurrencyBitcoinIcon style={{ fontSize: '1.2rem' }} />,
    desc: 'BTC deposit'
  }
];

const INPERSON_METHODS: {
  key: PaymentMethod;
  label: string;
  icon: React.ReactNode;
  desc: string;
}[] = [
  {
    key: 'cage',
    label: 'Cage Deposit',
    icon: <AccountBalanceIcon style={{ fontSize: '1.2rem' }} />,
    desc: 'In-person at a club'
  }
];

const DepositView = () => {
  const {
    selectedPool,
    setSelectedPool,
    selectedMethod,
    setSelectedMethod,
    amount,
    setAmount,
    step,
    setStep,
    closeCashier,
    awaitCashierSession,
    isOpen
  } = useContext(CashierContext);

  // Mirrors Unity CashiersMenuView's GO_TO_ACCOUNT_SETTINGS_BUTTON handler:
  // close the cashier and route the player to ProfilePanel with the
  // streetAddress field focused. SaloonHeader listens on 'open-profile-panel'
  // and re-emits 'focus-profile-field' once the panel mounts.
  const goToProfileForAddress = () => {
    closeCashier();
    window.dispatchEvent(
      new CustomEvent('open-profile-panel', { detail: { field: 'streetAddress' } })
    );
  };
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const limits = useCashierLimits();
  const contentRef = useRef<HTMLDivElement>(null);
  const [promoCode, setPromoCode] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [copied, setCopied] = useState(false);
  const [selectedClub, setSelectedClub] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const { clubs } = useClubs();

  // Promo-code validation (Unity parity). A code must be Applied and verified by
  // the backoffice before the deposit can continue; invalid / expired / used
  // codes surface an error and block Continue. Empty (optional) promo → no gate.
  const [promoStatus, setPromoStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>(
    'idle'
  );
  const [promoError, setPromoError] = useState('');
  // Bonus amount the backoffice grants for a valid code (data.data.rewards),
  // shown alongside "Promo code applied" so the player sees what they'll get.
  const [promoRewards, setPromoRewards] = useState<number | null>(null);
  // A verified promo is tied to the amount it was checked against; when the
  // amount changes it must be re-applied (the backoffice enforces min-deposit).
  useEffect(() => {
    setPromoStatus((s) => (s === 'valid' ? 'idle' : s));
  }, [amount]);
  // A non-empty code that hasn't verified clean blocks the deposit.
  const promoBlocksContinue = promoCode.trim() !== '' && promoStatus !== 'valid';

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoStatus('validating');
    setPromoError('');
    const result = await verifyPromoCode({
      playerGUID,
      promoCode,
      amount,
      jwt: getDescopeToken()
    });
    if (result.valid) {
      setPromoStatus('valid');
      setPromoError('');
      setPromoRewards(result.rewards);
    } else {
      setPromoStatus('invalid');
      setPromoError(result.message);
      setPromoRewards(null);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoStatus('idle');
    setPromoError('');
    setPromoRewards(null);
  };

  const handlePromoChange = (next: string) => {
    setPromoCode(next);
    // Editing the code invalidates a prior verdict — force a re-apply.
    setPromoStatus('idle');
    setPromoError('');
    setPromoRewards(null);
  };

  // Clear the transient deposit inputs when the cashier is exited (Unity
  // parity). `amount` lives in CashierContext and is already reset by
  // openCashier(); these local fields (custom amount, promo code) otherwise
  // survive because the cashier panel stays mounted while hidden, so reopening
  // showed a stale amount/promo with a "$0.00" (context-reset) Continue button.
  useEffect(() => {
    if (!isOpen) {
      setCustomAmount('');
      setPromoCode('');
      setPromoStatus('idle');
      setPromoError('');
      setPromoRewards(null);
      setSelectedClub('');
    }
  }, [isOpen]);
  const { profile, fetchProfile, error: profileError } = usePlayerProfile('cashier-deposit');
  const [profilePending, setProfilePending] = useState(true);
  const [profileRetry, setProfileRetry] = useState(0);

  // The cashier stays mounted while hidden; only load its profile when opened.
  useEffect(() => {
    if (!isOpen) return;
    const opening = new AbortController();
    setProfilePending(true);
    const loadProfile = async () => {
      await fetchProfile({ signal: opening.signal });
      if (!opening.signal.aborted) setProfilePending(false);
    };
    void loadProfile();
    return () => {
      opening.abort();
      setProfilePending(true);
    };
  }, [isOpen, fetchProfile, profileRetry]);

  const [showCardForm, setShowCardForm] = useState(false);
  const [depositError, setDepositError] = useState('');
  // Authorize.net transaction reference returned by create-credit-card-transaction,
  // surfaced on the success screen for parity with the Unity/WebGL payments app
  // (successTransaction.tsx "Transaction ID: {transId}").
  const [cardTransactionId, setCardTransactionId] = useState('');

  // Pre-deposit risk gate state — mirrors CashiersDepositView.cs:276 (PostRisk
  // on every deposit, regardless of channel). 'preDeposit' tracks whether a
  // KYC modal opened from the gate chain (continue the deposit on pass) vs the
  // card-submit flow (resume card payment on pass).
  const { runRisk, loading: riskLoading } = useRiskAssessment();
  const [gateLoading, setGateLoading] = useState(false);
  const [kycPostFlow, setKycPostFlow] = useState<'preDeposit' | 'postInitiate'>('postInitiate');

  // KYC state
  const [showKYC, setShowKYC] = useState(false);
  const [kycQuestions, setKycQuestions] = useState<any>(null);
  const [kycRequestId, setKycRequestId] = useState('');
  const [kycLoading, setKycLoading] = useState(false);
  // Marks the 'error' step as a terminal identity-check outcome (failed questionnaire
  // or lockdown). Unity's KycQuizView shows a dismiss-only AssessmentFailedView with a
  // "Go Back" button — never a "Try Again" that would re-attempt and trip the lockdown.
  const [kycTerminal, setKycTerminal] = useState(false);
  const [pendingCardData, setPendingCardData] = useState<any>(null);

  // Crypto state
  const [cryptoAddress, setCryptoAddress] = useState('');
  const [cryptoLoading, setCryptoLoading] = useState(false);
  const [cryptoError, setCryptoError] = useState('');

  // Pre-deposit risk gate. Mirrors CashiersDepositView.cs:276 — Unity calls
  // PostRisk for PAYMENT with kycRequired branching. On kycRequired=true the
  // KYC quiz opens; passing the quiz then continues the deposit
  // (kycPostFlow='preDeposit'). On kycRequired=false the deposit continues
  // directly. Deposits skip SMS MFA to match Unity, whose card deposits never
  // ran it; only withdrawals require MFA.
  const runDepositGateChain = async (): Promise<void> => {
    // A promo code was entered but hasn't verified clean — don't proceed
    // (defensive; the Continue button is also disabled in this state).
    if (promoBlocksContinue) {
      setPromoStatus('invalid');
      setPromoError((prev) => prev || 'Apply the promo code or remove it to continue.');
      return;
    }
    setGateLoading(true);
    setDepositError('');
    try {
      // Gate 1: mailing address (Unity CashiersMenuView.ButtonToDeposit{,Card}Tapped).
      if (await checkAddressRequired()) {
        setDepositError(ADDRESS_REQUIRED_TRANSACTION_MESSAGE);
        setStep('error');
        return;
      }

      // Session was profiled when the Deposit tab activated; reuse it.
      const sessionId = await awaitCashierSession();
      const risk = await runRisk({
        sessionId,
        eventType: 'payment',
        playerGuid: playerGUID,
        amount,
        pageId: TMXPageData.Deposit
      });

      if (!risk.ok) {
        setDepositError(risk.message);
        setStep('error');
        return;
      }

      if (risk.kycRequired) {
        setKycPostFlow('preDeposit');
        await fetchKycQuestions();
        return;
      }

      await processDeposit();
    } finally {
      setGateLoading(false);
    }
  };

  // After the gate chain, either show card form or process cage deposit
  const processDeposit = async () => {
    if (selectedMethod === 'credit_card') {
      // Show the credit card form
      setShowCardForm(true);
      return;
    }

    setStep('processing');

    if (selectedMethod === 'cage') {
      // Call cage deposit API
      try {
        const base = SOCKET_SERVER_URL?.replace(/^wss?:/, 'https:').replace(/\/+$/, '') || '';
        const jwt = getDescopeToken();

        const res = await fetch(`${base}/api/clubDeposit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${jwt}`
          },
          body: JSON.stringify({
            player_id: playerGUID,
            category: 'cage',
            type: 'deposit',
            currency: 'USD',
            amount: String(amount),
            affiliateId: selectedClub,
            promoCode: promoCode || '',
            token: jwt,
            jwt: jwt
          })
        });

        const json = await res.json();
        const txData = json?.data?.data || json?.data;
        if ((json?.success || json?.data?.rc === '0') && txData?.code) {
          limits.refresh();
          setConfirmationCode(String(txData.code));
          setStep('success');
        } else {
          console.error('[Deposit] Unexpected response:', json);
          setStep('error');
        }
      } catch (e) {
        console.error('[Deposit] Cage deposit failed:', errorToLogObject(e));
        setStep('error');
      }
    }
  };

  // Credit card payment submitted from card form
  const handleCardPayment = async (cardData: {
    cardNumber: string;
    expirationDate: string;
    cardCode: string;
    firstName: string;
    lastName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  }) => {
    setShowCardForm(false);
    setStep('processing');
    setDepositError('');

    try {
      const backofficeUrl = process.env.REACT_APP_BACKOFFICE_URL || `https://backoffice.${process.env.REACT_APP_DOMAIN || 'hijack.poker'}`;
      const jwt = getDescopeToken();

      // Get client IP for fraud prevention
      let clientIp = '';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        clientIp = ipData.ip;
      } catch {
        /* noop */
      }

      const res = await fetch(`${backofficeUrl}/api/payments/create-credit-card-transaction`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
          'X-Original-Client-IP': clientIp
        },
        body: JSON.stringify({
          amount: amount,
          email: profile.email || '',
          guid: playerGUID,
          cardNumber: cardData.cardNumber.replace(/\s/g, ''),
          expirationDate: cardData.expirationDate,
          cardCode: cardData.cardCode,
          billingFirstName: cardData.firstName,
          billingLastName: cardData.lastName,
          billingAddress: cardData.address,
          billingCity: cardData.city,
          billingState: cardData.state,
          billingZip: cardData.zip,
          billingCountry: cardData.country,
          rewardAmount: '',
          promoCode: promoCode || '',
          token: jwt
        })
      });

      const json = await res.json();
      const status = json?.data?.status;
      // Same field the payments app reads (custompayment.tsx: response.data.data.transactionId).
      const transactionId = json?.data?.transactionId;

      if (status === 'approved') {
        limits.refresh();
        setCardTransactionId(transactionId ? String(transactionId) : '');
        setStep('success');
        // AppsFlyer first_deposit (once/install) — real completed card deposit,
        // mirrors Unity's DepositSuccessEvent. Cage "requested" deposits don't count.
        logAppsFlyerEvent(AF_EVENT.FIRST_DEPOSIT, { once: true });
      } else if (status === 'pending') {
        limits.refresh();
        setCardTransactionId(transactionId ? String(transactionId) : '');
        setStep('success');
        logAppsFlyerEvent(AF_EVENT.FIRST_DEPOSIT, { once: true });
      } else {
        // Parse error details from Authorize.net response
        const details = json?.data?.details || json?.details || {};
        const avsCode = details?.avsResult?.code || '';
        const cvvCode = details?.cvvResult?.code || '';

        let msg = json?.data?.message || json?.message || 'Transaction was declined.';

        // Add AVS/CVV context for common failures
        if (avsCode && !['X', 'Y'].includes(avsCode)) {
          msg += ' Address verification failed — please check your billing address.';
        }
        if (cvvCode && cvvCode !== 'M') {
          msg += ' CVV verification failed — please check your security code.';
        }

        // Check for validation errors from Laravel
        if (json?.data?.errors?.item) {
          const fieldErrors = json.data.errors.item;
          const firstField = Object.keys(fieldErrors)[0];
          if (firstField && Array.isArray(fieldErrors[firstField])) {
            msg = fieldErrors[firstField][0];
          }
        }

        // Check for Authorize.net error text
        if (json?.error?.errorText) {
          msg = json.error.errorText;
        }

        setDepositError(msg);
        setStep('error');
      }
    } catch (e: any) {
      console.error('[Deposit] Card payment failed:', errorToLogObject(e));

      // Check for KYC required in error response
      try {
        const errData = (await e?.response?.json?.()) || e?.data || {};
        if (errData?.kycRequired === true) {
          console.log('[Deposit] KYC required — fetching questions');
          setPendingCardData(cardData);
          await fetchKycQuestions();
          return;
        }
      } catch {
        /* noop */
      }

      let msg = 'Payment failed. Please check your details and try again.';
      if (e?.message) msg = e.message;
      setDepositError(msg);
      setStep('error');
    }
  };

  // KYC functions
  const getBackofficeUrl = () => {
    return process.env.REACT_APP_BACKOFFICE_URL || `https://backoffice.${process.env.REACT_APP_DOMAIN || 'hijack.poker'}`;
  };

  const fetchKycQuestions = async () => {
    setKycLoading(true);
    setKycTerminal(false);
    try {
      const jwt = getDescopeToken();
      const res = await fetch(
        `${getBackofficeUrl()}/api/risk/kyc/questions?playerGuid=${playerGUID}`,
        {
          headers: { Authorization: `Bearer ${jwt}` }
        }
      );

      // Any non-success is a terminal identity-check outcome. Mirror Unity's
      // GetKYCQuestion → OnKycQuestionEvent exactly: prefer the backend `message`,
      // fall back to `error`, then ERROR_MESSAGE (Unity's precedence/fallback).
      if (!res.ok) {
        const data = await safeReadJson(res);
        setDepositError(pickApiErrorMessage(data, ERROR_MESSAGE));
        setKycTerminal(true);
        setStep('error');
        return;
      }

      const data = await res.json();
      setKycQuestions(data.questions);
      setKycRequestId(data.requestId);
      setShowKYC(true);
      setStep('select'); // Reset step so modal shows cleanly
    } catch (e) {
      console.error('[Deposit] KYC fetch failed:', errorToLogObject(e));
      setDepositError(CONNECTION_ERROR_MESSAGE);
      setKycTerminal(true);
      setStep('error');
    } finally {
      setKycLoading(false);
    }
  };

  const handleKycSubmit = async (answers: any) => {
    // A failed questionnaire is terminal — matching Unity's KycQuizView, which swaps
    // the quiz for a terminal AssessmentFailedView with no resubmit path. Close the
    // modal and route to the terminal 'error' step so the same requestId can't be
    // re-submitted; retrying means restarting the flow for a fresh requestId.
    const terminateKyc = (message: string) => {
      setShowKYC(false);
      setKycTerminal(true);
      setStep('error');
      setDepositError(message);
    };

    setKycLoading(true);
    try {
      const jwt = getDescopeToken();
      const res = await fetch(`${getBackofficeUrl()}/api/risk/kyc/questions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify({
          playerGuid: playerGUID,
          requestId: kycRequestId,
          answers: answers
        })
      });

      const data = await safeReadJson(res);

      // Distinguish a transport/server error from a successful answer-rejection,
      // matching Unity's LexisNexisModel.PostKYCAnswerToQuestion: HTTP non-success
      // routes through onFailure with the backend-supplied message, while a 200
      // with reviewStatus != PASS is a fixed "verification failed" UX state.
      if (!res.ok) {
        console.error('[Deposit] KYC submit failed:', res.status, data);
        terminateKyc(pickApiErrorMessage(data, ERROR_MESSAGE));
        return;
      }

      const reviewStatus =
        data && typeof data === 'object' ? (data as any).reviewStatus : undefined;

      if (reviewStatus === 'pass') {
        setShowKYC(false);
        if (kycPostFlow === 'preDeposit') {
          // KYC was triggered by the pre-deposit risk check — continue the deposit.
          setKycPostFlow('postInitiate');
          await processDeposit();
        } else if (pendingCardData) {
          // KYC surfaced from the card-payment 403 path — resume the original card submit.
          await handleCardPayment(pendingCardData);
        }
      } else {
        terminateKyc(IDENTITY_CHECK_FAILED_MESSAGE);
      }
    } catch (e) {
      console.error('[Deposit] KYC submit network error:', errorToLogObject(e));
      terminateKyc(ERROR_MESSAGE);
    } finally {
      setKycLoading(false);
    }
  };

  // Crypto state for QR code image URL from API
  const [cryptoQrUrl, setCryptoQrUrl] = useState('');

  useEffect(() => {
    if (selectedMethod !== 'crypto') {
      return;
    }

    const savedAddress = normalizeWalletAddress(profile.walletAddress);
    if (savedAddress && savedAddress !== cryptoAddress) {
      setCryptoAddress(savedAddress);
      setCryptoQrUrl('');
      setCryptoError('');
    }
  }, [cryptoAddress, profile.walletAddress, selectedMethod]);

  const getMethodLimit = (method: PaymentMethod) => {
    if (method === 'credit_card') return limits.deposit.methods.creditCard;
    if (method === 'crypto') return limits.deposit.methods.crypto;
    return limits.deposit.methods.cage;
  };

  // Fetch or create crypto deposit address. Unity gates this on
  // address-required at the menu-tap level (CashiersMenuView.ButtonToDeposit
  // BitCoinTapped); fetchCryptoAddress is the equivalent first transactional
  // action in React.
  const fetchCryptoAddress = useCallback(async () => {
    setCryptoLoading(true);
    setCryptoError('');
    setCryptoQrUrl('');
    try {
      if (await checkAddressRequired()) {
        setCryptoError(ADDRESS_REQUIRED_TRANSACTION_MESSAGE);
        return;
      }

      const jwt = getDescopeToken();
      const res = await fetch(`${getBackofficeUrl()}/api/v2/forumpay/deposit/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`
        },
        body: JSON.stringify({
          currency: 'BTC',
          jwt: jwt
        })
      });

      const json = await res.json();
      if (json?.success && json?.data?.address) {
        setCryptoAddress(json.data.address);
        setCryptoQrUrl(json.data.qr_code_img || json.data.qr_img || '');
      } else {
        setCryptoError(json?.message || 'Failed to generate deposit address.');
      }
    } catch (e) {
      console.error('[Deposit] Crypto address fetch failed:', errorToLogObject(e));
      setCryptoError('Could not generate a deposit address. Please try again.');
    } finally {
      setCryptoLoading(false);
    }
  }, []);

  const pool = selectedPool === 'digital' ? limits.deposit.digital : limits.deposit.inperson;
  const methods = selectedPool === 'digital' ? DIGITAL_METHODS : INPERSON_METHODS;
  const cardMaxAmount = limits.deposit.methods.creditCard.remaining;
  const cageMaxAmount = limits.deposit.methods.cage.remaining;
  const selectedMethodLimit =
    selectedMethod === 'credit_card'
      ? limits.deposit.methods.creditCard
      : selectedMethod === 'crypto'
      ? limits.deposit.methods.crypto
      : pool;

  // Animate content transitions
  useEffect(() => {
    if (contentRef.current) {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
      );
    }
  }, [step, selectedPool, selectedMethod]);

  const goBack = () => {
    if (selectedMethod) {
      setSelectedMethod(null as any);
    } else if (selectedPool) {
      setSelectedPool(null as any);
    }
    setAmount(0);
    setCustomAmount('');
    // A promo code + club selection are entered per deposit attempt — clear them
    // on Back so they don't carry over to a different method/amount (matches
    // Back to Cashier).
    setPromoCode('');
    setPromoStatus('idle');
    setPromoError('');
    setPromoRewards(null);
    setSelectedClub('');
  };

  // "Back to Cashier" from the success screen — reset the whole deposit flow to
  // the method-selection menu so the player stays in the Cashier (the header
  // back arrow instead exits to the Lobby).
  const returnToCashierMenu = () => {
    setSelectedMethod(null as any);
    setSelectedPool(null as any);
    setAmount(0);
    setStep('select');
    setCustomAmount('');
    setPromoCode('');
    setPromoStatus('idle');
    setPromoError('');
    setPromoRewards(null);
    setSelectedClub('');
    setConfirmationCode('');
    setCardTransactionId('');
  };

  // KYC modal (overlays everything when shown)
  if (showKYC) {
    return (
      <KYCModal
        open={showKYC}
        questions={kycQuestions}
        onSubmit={handleKycSubmit}
        onClose={() => {
          setShowKYC(false);
          setStep('error');
          setDepositError('Identity verification is required to complete this deposit.');
        }}
        loading={kycLoading}
      />
    );
  }

  // Credit card form (shown after the gate chain)
  if (showCardForm) {
    return (
      <CreditCardForm
        amount={amount}
        onSubmit={handleCardPayment}
        onBack={() => setShowCardForm(false)}
      />
    );
  }

  // Processing
  if (step === 'processing') {
    return <ProcessingView amount={amount} onComplete={() => {}} />;
  }

  // Error
  if (step === 'error') {
    // Unity parity: a failed/locked-out identity check is terminal (KycQuizView's
    // AssessmentFailedView). It offers only a dismiss ("Go Back") — never a "Try
    // Again", which would re-attempt and walk the player straight into the lockdown.
    if (kycTerminal) {
      return (
        <div style={centeredView}>
          <span style={{ fontSize: '2rem' }}>⚠️</span>
          <span style={{ ...successLabel, color: '#c0392b' }}>Identity Check</span>
          <span style={successSub}>{depositError || IDENTITY_CHECK_FAILED_MESSAGE}</span>
          <button
            onClick={() => {
              setKycTerminal(false);
              setStep('select');
            }}
            style={{ ...continueBtn, marginTop: '12px', maxWidth: '200px' }}>
            Go Back
          </button>
        </div>
      );
    }

    const isAddressMissing = depositError === ADDRESS_REQUIRED_TRANSACTION_MESSAGE;
    return (
      <div style={centeredView}>
        <span style={{ fontSize: '2rem' }}>⚠️</span>
        <span style={{ ...successLabel, color: '#c0392b' }}>Deposit Failed</span>
        <span style={successSub}>{depositError || 'Something went wrong. Please try again.'}</span>
        {isAddressMissing && (
          <button
            onClick={goToProfileForAddress}
            style={{ ...continueBtn, marginTop: '12px', maxWidth: '240px' }}>
            {GO_TO_ACCOUNT_SETTINGS_BUTTON}
          </button>
        )}
        <button
          onClick={() => setStep('select')}
          style={{ ...continueBtn, marginTop: '12px', maxWidth: '200px' }}>
          Try Again
        </button>
      </div>
    );
  }

  // Success
  if (step === 'success') {
    return (
      <SuccessView
        amount={amount}
        confirmationCode={confirmationCode}
        transactionId={cardTransactionId}
        isCage={selectedMethod === 'cage'}
        onDone={returnToCashierMenu}
      />
    );
  }

  if (step === 'select' && (profilePending || profileError)) {
    return (
      <div style={centeredView}>
        {profilePending ? (
          <span role='status' style={processingText}>Loading cashier profile...</span>
        ) : (
          <>
            <span role='alert' style={processingText}>Your profile is temporarily unavailable. Please try again.</span>
            <button style={successDoneBtn} onClick={() => setProfileRetry((attempt) => attempt + 1)}>
              Retry
            </button>
          </>
        )}
      </div>
    );
  }

  if (limits.loading && step === 'select') {
    return (
      <div style={centeredView}>
        <div style={spinner} />
        <span style={processingText}>Loading cashier limits...</span>
      </div>
    );
  }

  return (
    <div ref={contentRef} style={containerStyle}>
      {/* Step 1: Pick pool */}
      {!selectedPool && (
        <>
          <div style={sectionLabel}>Choose deposit type</div>
          <div style={poolCardsRow}>
            <PoolCard
              label='Digital'
              icon={<DevicesIcon style={{ fontSize: '1.5rem', color: '#63b3ed' }} />}
              subtitle='Card, Crypto (separate limits)'
              limit={limits.deposit.digital}
              onClick={() => setSelectedPool('digital')}
            />
            <PoolCard
              label='In-Person'
              icon={<StorefrontIcon style={{ fontSize: '1.5rem', color: '#E98C26' }} />}
              subtitle='Cage at club'
              limit={limits.deposit.inperson}
              onClick={() => setSelectedPool('inperson')}
            />
          </div>
          <TierExplainer
            tierInfo={limits.tierInfo}
            handsPlayed={limits.handsPlayed}
            idVerified={limits.idVerified}
          />
        </>
      )}

      {/* Step 2: Pick method */}
      {selectedPool && !selectedMethod && (
        <>
          <button onClick={goBack} style={backBtn}>
            ← Back
          </button>
          <PoolHeader pool={pool} label={selectedPool === 'digital' ? 'Digital' : 'In-Person'} />

          <div style={sectionLabel}>Payment Method</div>
          {methods.map((m) => (
            <button key={m.key} onClick={() => setSelectedMethod(m.key)} style={methodCard}>
              <div style={methodIconWrap}>{m.icon}</div>
              <div style={methodInfo}>
                <span style={methodLabel}>{m.label}</span>
                <span style={methodDesc}>{m.desc}</span>
                <MethodUsageBar limit={getMethodLimit(m.key)} />
              </div>
            </button>
          ))}
        </>
      )}

      {/* Step 3a: Credit Card — amount + promo */}
      {selectedMethod === 'credit_card' && (
        <>
          <button onClick={goBack} style={backBtn}>
            ← Back
          </button>
          <PoolHeader pool={selectedMethodLimit} label='Credit Card Deposit' />

          <div style={sectionLabel}>Amount</div>
          <div style={quickAmountsRow}>
            {QUICK_AMOUNTS.filter((a) => a <= cardMaxAmount).map((a) => (
              <button
                key={a}
                onClick={() => {
                  setAmount(a);
                  setCustomAmount('');
                }}
                style={{ ...quickAmountBtn, ...(amount === a ? quickAmountActive : {}) }}>
                ${a}
              </button>
            ))}
          </div>
          <div style={customRow}>
            <span style={dollarSign}>$</span>
            <input
              type='text'
              inputMode='decimal'
              placeholder='Custom amount'
              value={customAmount}
              onChange={(e) => {
                const cleaned = sanitizeCurrencyAmount(e.target.value);
                setCustomAmount(cleaned);
                setAmount(parseCurrencyAmount(cleaned));
              }}
              style={customInput}
            />
          </div>
          {amount > cardMaxAmount && cardMaxAmount > 0 && (
            <div style={errorText}>
              Exceeds card deposit limit. Max: {currencyFormatter(cardMaxAmount)}
            </div>
          )}

          <PromoCodeField
            value={promoCode}
            status={promoStatus}
            error={promoError}
            rewards={promoRewards}
            onChange={handlePromoChange}
            onApply={handleApplyPromo}
            onRemove={handleRemovePromo}
          />

          <div style={infoNote}>
            Please use the same email and phone number associated with your Hijack account to ensure
            funds are directed correctly.
          </div>

          {/* Fee/restriction details (Unity/WebGL parity: payments amountInputPage.tsx
              Payment Information — processing charge, Hijack fee, Amex restriction). */}
          <div style={payInfoBox}>
            <div style={payInfoRow}>
              <span style={payInfoLabel}>Minimum deposit</span>
              <span style={payInfoValue}>$10.00</span>
            </div>
            <div style={payInfoRow}>
              <span style={payInfoLabel}>Processing charge</span>
              <span style={payInfoValue}>{`${CREDIT_CARD_PROCESSING_FEE_RATE * 100}%`}</span>
            </div>
            <div style={payInfoRow}>
              <span style={payInfoLabel}>Hijack fee</span>
              <span style={payInfoValue}>$0.00</span>
            </div>
            <div style={payInfoNote}>
              <strong>Note:</strong> American Express not accepted.
            </div>
          </div>

          <button
            onClick={runDepositGateChain}
            disabled={
              amount < 10 ||
              amount > cardMaxAmount ||
              gateLoading ||
              riskLoading ||
              promoBlocksContinue
            }
            style={{
              ...continueBtn,
              opacity:
                amount < 10 ||
                amount > cardMaxAmount ||
                gateLoading ||
                riskLoading ||
                promoBlocksContinue
                  ? 0.4
                  : 1
            }}>
            {gateLoading || riskLoading
              ? IDENTITY_PROCESS_CHECKING
              : `Continue · ${currencyFormatter(
                  amount + amount * CREDIT_CARD_PROCESSING_FEE_RATE
                )}`}
          </button>
        </>
      )}

      {/* Step 3b: Crypto (BTC only) — wallet address */}
      {selectedMethod === 'crypto' && (
        <>
          <button onClick={goBack} style={backBtn}>
            ← Back
          </button>
          <PoolHeader pool={selectedMethodLimit} label='Bitcoin Deposit' />

          {cryptoLoading && (
            <div style={centeredView}>
              <div style={spinner} />
              <span style={processingText}>Generating deposit address...</span>
            </div>
          )}

          {cryptoError && (
            <div style={centeredView}>
              <span style={{ fontSize: '2rem' }}>⚠️</span>
              <span style={{ ...successLabel, color: '#c0392b' }}>{cryptoError}</span>
              {cryptoError === ADDRESS_REQUIRED_TRANSACTION_MESSAGE && (
                <button
                  onClick={goToProfileForAddress}
                  style={{ ...continueBtn, maxWidth: '240px' }}>
                  {GO_TO_ACCOUNT_SETTINGS_BUTTON}
                </button>
              )}
              <button onClick={fetchCryptoAddress} style={{ ...continueBtn, maxWidth: '200px' }}>
                Try Again
              </button>
            </div>
          )}

          {!cryptoAddress && !cryptoLoading && !cryptoError && (
            <div style={walletRequestView}>
              <span style={walletRequestTitle}>Get Your Crypto Wallet Address</span>
              <button onClick={fetchCryptoAddress} style={walletRequestButton}>
                Get My BTC Wallet Address
              </button>
              <span style={walletRequestSub}>
                You can send any amount to your personal address
              </span>
              <span style={walletRequestDisclaimer}>
                For all crypto transactions, a variable conversion fee of approximately 6% will be
                applied to your final amount.
              </span>
            </div>
          )}

          {cryptoAddress && !cryptoLoading && (
            <>
              <div style={sectionLabel}>Send BTC to this address</div>
              <div style={walletAddressBox}>
                <div style={qrContainer}>
                  {cryptoQrUrl ? (
                    <img
                      src={cryptoQrUrl}
                      alt='BTC deposit QR'
                      style={{ width: 140, height: 140, borderRadius: '8px' }}
                    />
                  ) : (
                    <QRCodeSVG
                      value={`bitcoin:${cryptoAddress}`}
                      size={140}
                      bgColor='#ffffff'
                      fgColor='#000000'
                      level='M'
                      style={{ borderRadius: '8px' }}
                    />
                  )}
                </div>
                <div style={addressRow}>
                  <span style={addressText}>{cryptoAddress}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(cryptoAddress);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    style={copyBtn}>
                    <ContentCopyIcon style={{ fontSize: '0.85rem' }} />
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <span style={cryptoNote}>
                  Using your existing crypto wallet, send funds to the address above. Funds will be
                  converted to USD and credited to your account upon block verification.
                </span>
                <span style={cryptoDisclaimer}>
                  For all crypto transactions, a variable conversion fee of approximately 6% will be
                  applied to your final amount.
                </span>
                <span style={cryptoNote}>
                  Crypto deposits use their own threshold and are not part of the tiered online
                  credit card deposit pool.
                </span>
              </div>
            </>
          )}
        </>
      )}

      {/* Step 3c: Cage — amount + club + promo */}
      {selectedMethod === 'cage' && (
        <>
          <button onClick={goBack} style={backBtn}>
            ← Back
          </button>
          <PoolHeader pool={pool} label='Cage Deposit' />

          <div style={sectionLabel}>Amount</div>
          <div style={quickAmountsRow}>
            {QUICK_AMOUNTS.filter((a) => a <= cageMaxAmount).map((a) => (
              <button
                key={a}
                onClick={() => {
                  setAmount(a);
                  setCustomAmount('');
                }}
                style={{ ...quickAmountBtn, ...(amount === a ? quickAmountActive : {}) }}>
                ${a}
              </button>
            ))}
          </div>
          <div style={customRow}>
            <span style={dollarSign}>$</span>
            <input
              type='text'
              inputMode='decimal'
              placeholder='Custom amount'
              value={customAmount}
              onChange={(e) => {
                const cleaned = sanitizeCurrencyAmount(e.target.value);
                setCustomAmount(cleaned);
                setAmount(parseCurrencyAmount(cleaned));
              }}
              style={customInput}
            />
          </div>
          {amount > cageMaxAmount && cageMaxAmount > 0 && (
            <div style={errorText}>
              Exceeds in-person deposit limit. Max: {currencyFormatter(cageMaxAmount)}
            </div>
          )}

          <div style={{ ...sectionLabel, marginTop: '12px' }}>Club Location</div>
          <select
            value={selectedClub}
            onChange={(e) => setSelectedClub(e.target.value)}
            style={selectInput}>
            <option value=''>Select a club...</option>
            {clubs.map((club) => (
              <option key={club.key} value={club.locationId}>
                {club.clubName}
              </option>
            ))}
          </select>

          <PromoCodeField
            value={promoCode}
            status={promoStatus}
            error={promoError}
            rewards={promoRewards}
            onChange={handlePromoChange}
            onApply={handleApplyPromo}
            onRemove={handleRemovePromo}
          />

          <button
            onClick={runDepositGateChain}
            disabled={
              amount < 10 ||
              amount > cageMaxAmount ||
              !selectedClub ||
              gateLoading ||
              riskLoading ||
              promoBlocksContinue
            }
            style={{
              ...continueBtn,
              opacity:
                amount < 10 ||
                amount > cageMaxAmount ||
                !selectedClub ||
                gateLoading ||
                riskLoading ||
                promoBlocksContinue
                  ? 0.4
                  : 1
            }}>
            {gateLoading || riskLoading
              ? IDENTITY_PROCESS_CHECKING
              : `Continue · ${currencyFormatter(amount)}`}
          </button>
        </>
      )}
    </div>
  );
};

/**
 * Promo-code entry with inline validation (Unity parity). The player Applies a
 * code, which the backoffice verifies; a valid code locks the field and shows a
 * green confirmation, an invalid/expired/used code shows the reason. The parent
 * blocks Continue until a non-empty code verifies clean.
 */
const PromoCodeField = ({
  value,
  status,
  error,
  rewards,
  onChange,
  onApply,
  onRemove
}: {
  value: string;
  status: 'idle' | 'validating' | 'valid' | 'invalid';
  error: string;
  // Bonus amount the valid code grants (null when unknown); appended to the
  // "applied" confirmation so the player sees the promo's value.
  rewards: number | null;
  onChange: (v: string) => void;
  onApply: () => void;
  onRemove: () => void;
}) => {
  const trimmed = value.trim();
  const isValid = status === 'valid';
  const isValidating = status === 'validating';
  return (
    <>
      <div style={{ ...sectionLabel, marginTop: '12px' }}>Promo Code (optional)</div>
      <div style={promoRow}>
        <input
          type='text'
          placeholder='Enter code'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={isValid}
          style={{ ...promoInput, flex: 1, ...(isValid ? promoInputValid : {}) }}
        />
        {isValid ? (
          <button type='button' onClick={onRemove} style={promoRemoveBtn}>
            Remove
          </button>
        ) : (
          <button
            type='button'
            onClick={onApply}
            disabled={!trimmed || isValidating}
            style={{ ...promoApplyBtn, opacity: !trimmed || isValidating ? 0.5 : 1 }}>
            {isValidating ? 'Checking…' : 'Apply'}
          </button>
        )}
      </div>
      {isValid && (
        <div style={promoValidText}>
          <CheckCircleIcon style={{ fontSize: '0.95rem' }} /> {promoAppliedLabel(rewards)}
        </div>
      )}
      {status === 'invalid' && !!error && <div style={{ ...errorText, marginTop: '6px' }}>{error}</div>}
    </>
  );
};

/** Pool header with limit bar */
const PoolHeader = ({
  pool,
  label
}: {
  pool: { dailyLimit: number; remaining: number; used: number };
  label: string;
}) => (
  <div style={poolHeaderStyle}>
    <span style={poolTitle}>{label}</span>
    <div style={limitBarWrap}>
      <div style={limitTrack}>
        <div style={{ ...limitFill, width: `${(pool.used / pool.dailyLimit) * 100}%` }} />
      </div>
      <span style={limitText}>
        {currencyFormatter(pool.remaining)} of {currencyFormatter(pool.dailyLimit)} remaining today
      </span>
    </div>
  </div>
);

/** Pool selection card */
const PoolCard = ({
  label,
  icon,
  subtitle,
  limit,
  onClick
}: {
  label: string;
  icon: React.ReactNode;
  subtitle: string;
  limit: { dailyLimit: number; remaining: number; used: number };
  onClick: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const pct = limit.dailyLimit > 0 ? (limit.used / limit.dailyLimit) * 100 : 0;

  useEffect(() => {
    if (ref.current) {
      gsap.fromTo(
        ref.current,
        { opacity: 0, scale: 0.95 },
        { opacity: 1, scale: 1, duration: 0.3, ease: 'back.out(1.3)' }
      );
    }
  }, []);

  return (
    <div ref={ref} onClick={onClick} style={poolCardStyle}>
      {icon}
      <span style={poolCardLabel}>{label}</span>
      <span style={poolCardSub}>{subtitle}</span>
      <div style={poolBarTrack}>
        <div style={{ ...poolBarFill, width: `${pct}%` }} />
      </div>
      <span style={poolRemaining}>{currencyFormatter(limit.remaining)} left</span>
    </div>
  );
};

const MethodUsageBar = ({
  limit
}: {
  limit: { used: number; dailyLimit: number; usagePercent?: number };
}) => (
  <div style={methodUsageWrap}>
    <div style={methodUsageTrack}>
      <div style={{ ...methodUsageFill, width: `${limit.usagePercent ?? 0}%` }} />
    </div>
    <span style={methodUsageText}>
      {currencyFormatter(limit.used)} / {currencyFormatter(limit.dailyLimit)}
    </span>
  </div>
);

/** Processing animation */
const ProcessingView = ({ amount, onComplete }: { amount: number; onComplete: () => void }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const tl = gsap.timeline({ onComplete });
    tl.fromTo(ref.current, { opacity: 0 }, { opacity: 1, duration: 0.3 });
    tl.to({}, { duration: 2 });
  }, [onComplete]);

  return (
    <div ref={ref} style={centeredView}>
      <div style={spinner} />
      <span style={processingText}>Processing {currencyFormatter(amount)} deposit...</span>
    </div>
  );
};

/** Success view */
const SuccessView = ({
  amount,
  confirmationCode,
  transactionId,
  isCage,
  onDone
}: {
  amount: number;
  confirmationCode?: string;
  transactionId?: string;
  isCage?: boolean;
  onDone: () => void;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      gsap.fromTo(
        ref.current,
        { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1, duration: 0.5, ease: 'back.out(1.5)' }
      );
    }
  }, []);

  return (
    <div ref={ref} style={centeredView}>
      <CheckCircleIcon style={{ fontSize: '3rem', color: '#4CAF50' }} />
      <span style={successAmount}>{currencyFormatter(amount)}</span>
      <span style={successLabel}>{isCage ? 'Deposit Requested' : 'Deposit Successful'}</span>

      {isCage && confirmationCode ? (
        <>
          <span style={successSub}>Present this code at the cage to complete your deposit.</span>
          <div style={codeDisplayRow}>
            {confirmationCode.split('').map((char, i) => (
              <div key={i} style={codeDisplayDigit}>
                {char}
              </div>
            ))}
          </div>
          <span style={successSub}>A copy has also been sent to your email.</span>
        </>
      ) : (
        <span style={successSub}>Your balance has been updated.</span>
      )}

      {/* Transaction reference (Unity/WebGL parity: payments successTransaction.tsx
          "Transaction ID: {transId}"). Card deposits only — cage uses the code above. */}
      {!isCage && transactionId ? (
        <span style={transactionIdText}>Transaction ID: {transactionId}</span>
      ) : null}

      {/* Keep the player inside the Cashier after a transaction (Unity parity) —
          the header back arrow exits to the Lobby, so offer an explicit return
          to the deposit menu here. */}
      <button onClick={onDone} style={successDoneBtn}>
        Back to Cashier
      </button>
    </div>
  );
};

export default DepositView;

/* ── Styles ── */

const containerStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '12px' };
const sectionLabel: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: 'rgba(255,255,255,0.4)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em'
};
const backBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'rgba(255,255,255,0.5)',
  fontSize: '0.75rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  padding: 0,
  alignSelf: 'flex-start'
};

/* Pool cards */
const poolCardsRow: CSSProperties = { display: 'flex', gap: '10px' };
const poolCardStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '6px',
  padding: '16px 12px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '14px',
  cursor: 'pointer',
  transition: 'all 0.2s ease'
};
const poolCardLabel: CSSProperties = { fontSize: '0.9rem', fontWeight: 700, color: '#fff' };
const poolCardSub: CSSProperties = { fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)' };
const poolBarTrack: CSSProperties = {
  width: '100%',
  height: 4,
  backgroundColor: 'rgba(255,255,255,0.08)',
  borderRadius: 2,
  overflow: 'hidden',
  marginTop: '4px'
};
const poolBarFill: CSSProperties = { height: '100%', borderRadius: 2, backgroundColor: '#E98C26' };
const poolRemaining: CSSProperties = { fontSize: '0.65rem', fontWeight: 600, color: '#4CAF50' };

/* Pool header */
const poolHeaderStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '8px' };
const poolTitle: CSSProperties = { fontSize: '1rem', fontWeight: 700, color: '#fff' };
const limitBarWrap: CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px' };
const limitTrack: CSSProperties = {
  width: '100%',
  height: 5,
  backgroundColor: 'rgba(255,255,255,0.08)',
  borderRadius: 3,
  overflow: 'hidden'
};
const limitFill: CSSProperties = { height: '100%', borderRadius: 3, backgroundColor: '#E98C26' };
const limitText: CSSProperties = { fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' };

/* Method cards */
const methodCard: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  padding: '12px 14px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.15s ease',
  marginBottom: '6px'
};
const methodIconWrap: CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '10px',
  backgroundColor: 'rgba(255,255,255,0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'rgba(255,255,255,0.7)',
  flexShrink: 0
};
const methodInfo: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left'
};
const methodLabel: CSSProperties = { fontSize: '0.85rem', fontWeight: 600, color: '#fff' };
const methodDesc: CSSProperties = { fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)' };
const methodUsageWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  marginTop: '6px'
};
const methodUsageTrack: CSSProperties = {
  width: '100%',
  height: 4,
  backgroundColor: 'rgba(255,255,255,0.08)',
  borderRadius: 999,
  overflow: 'hidden'
};
const methodUsageFill: CSSProperties = {
  height: '100%',
  borderRadius: 999,
  backgroundColor: '#4CAF50'
};
const methodUsageText: CSSProperties = { fontSize: '0.6rem', color: 'rgba(255,255,255,0.45)' };

/* Amount input */
const quickAmountsRow: CSSProperties = { display: 'flex', gap: '8px', flexWrap: 'wrap' };
const quickAmountBtn: CSSProperties = {
  flex: '1 1 auto',
  minWidth: '50px',
  padding: '10px 0',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: 'rgba(255,255,255,0.8)',
  fontSize: '0.85rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const quickAmountActive: CSSProperties = {
  backgroundColor: 'rgba(233,140,38,0.2)',
  borderColor: '#E98C26',
  color: '#E98C26'
};
const customRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  padding: '0 12px'
};
const dollarSign: CSSProperties = {
  fontSize: '1rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.4)'
};
const customInput: CSSProperties = {
  flex: 1,
  background: 'none',
  border: 'none',
  color: '#fff',
  fontSize: '1rem',
  fontWeight: 600,
  padding: '12px 0',
  outline: 'none',
  fontFamily: 'inherit'
};
const errorText: CSSProperties = { fontSize: '0.7rem', color: '#c0392b', fontWeight: 600 };

/* Promo + continue */
const promoInput: CSSProperties = {
  width: '100%',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '10px',
  color: '#fff',
  fontSize: '0.85rem',
  padding: '10px 12px',
  outline: 'none',
  fontFamily: 'inherit',
  boxSizing: 'border-box'
};
const selectInput: CSSProperties = {
  ...promoInput,
  backgroundColor: '#1a2332',
  appearance: 'auto' as any
};
const promoRow: CSSProperties = { display: 'flex', gap: '8px', alignItems: 'stretch' };
const promoInputValid: CSSProperties = {
  border: '1px solid rgba(88, 214, 141, 0.5)',
  color: 'rgba(255,255,255,0.7)'
};
const promoApplyBtn: CSSProperties = {
  flexShrink: 0,
  padding: '0 16px',
  background: 'rgba(233, 140, 38, 0.15)',
  border: '1px solid rgba(233, 140, 38, 0.4)',
  borderRadius: '10px',
  color: '#E98C26',
  fontSize: '0.8rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const promoRemoveBtn: CSSProperties = {
  ...promoApplyBtn,
  background: 'rgba(200, 60, 60, 0.15)',
  border: '1px solid rgba(200, 60, 60, 0.35)',
  color: '#e05555'
};
const promoValidText: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginTop: '6px',
  fontSize: '0.72rem',
  fontWeight: 600,
  color: '#58d68d'
};
const continueBtn: CSSProperties = {
  width: '100%',
  padding: '14px',
  backgroundColor: '#E98C26',
  border: 'none',
  borderRadius: '12px',
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
  marginTop: '8px'
};
const infoNote: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.5)',
  lineHeight: 1.4
};
const payInfoBox: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  padding: '12px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px'
};
const payInfoRow: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};
const payInfoLabel: CSSProperties = { fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' };
const payInfoValue: CSSProperties = { fontSize: '0.75rem', fontWeight: 700, color: '#fff' };
const payInfoNote: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.5)',
  lineHeight: 1.4,
  marginTop: '2px'
};

/* Crypto wallet */
const walletRequestView: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '14px',
  minHeight: 320,
  padding: '34px 16px 16px',
  textAlign: 'center'
};
const walletRequestTitle: CSSProperties = {
  fontSize: '1.15rem',
  fontWeight: 800,
  color: '#fff',
  lineHeight: 1.2
};
const walletRequestButton: CSSProperties = {
  ...continueBtn,
  width: 'min(100%, 340px)',
  marginTop: '8px',
  fontSize: '1rem'
};
const walletRequestSub: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.72)',
  textAlign: 'center',
  lineHeight: 1.4,
  fontWeight: 600
};
const walletRequestDisclaimer: CSSProperties = {
  fontSize: '0.65rem',
  color: '#E98C26',
  textAlign: 'center',
  lineHeight: 1.4,
  fontWeight: 600,
  marginTop: 'auto'
};
const walletAddressBox: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  padding: '20px',
  backgroundColor: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '14px'
};
const qrContainer: CSSProperties = {
  padding: '10px',
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};
const addressRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: '8px' };
const addressText: CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.7)',
  fontFamily: 'monospace',
  wordBreak: 'break-all'
};
const copyBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: '8px',
  color: 'rgba(255,255,255,0.7)',
  fontSize: '0.7rem',
  fontWeight: 600,
  padding: '6px 10px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  flexShrink: 0
};
const cryptoNote: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255,255,255,0.4)',
  textAlign: 'center',
  lineHeight: 1.4
};
const cryptoDisclaimer: CSSProperties = {
  fontSize: '0.65rem',
  color: '#E98C26',
  textAlign: 'center',
  lineHeight: 1.4,
  fontWeight: 600
};

/* Processing / success */
const centeredView: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '40px 0',
  textAlign: 'center'
};
const spinner: CSSProperties = {
  width: 40,
  height: 40,
  border: '3px solid rgba(255,255,255,0.1)',
  borderTopColor: '#E98C26',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite'
};
const processingText: CSSProperties = {
  fontSize: '0.9rem',
  color: 'rgba(255,255,255,0.7)',
  fontWeight: 600
};
const successAmount: CSSProperties = { fontSize: '2rem', fontWeight: 800, color: '#4CAF50' };
const successLabel: CSSProperties = { fontSize: '1rem', fontWeight: 700, color: '#fff' };
const successSub: CSSProperties = { fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' };
const transactionIdText: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: 'rgba(255,255,255,0.6)',
  fontFamily: 'monospace',
  marginTop: '4px',
  wordBreak: 'break-all',
  textAlign: 'center'
};
const successDoneBtn: CSSProperties = {
  marginTop: '20px',
  padding: '12px 28px',
  background: 'rgba(233, 140, 38, 0.15)',
  border: '1px solid rgba(233, 140, 38, 0.4)',
  borderRadius: '10px',
  color: '#E98C26',
  fontSize: '0.9rem',
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit'
};
const codeDisplayRow: CSSProperties = {
  display: 'flex',
  gap: '6px',
  justifyContent: 'center',
  margin: '12px 0'
};
const codeDisplayDigit: CSSProperties = {
  width: 40,
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255,255,255,0.06)',
  border: '2px solid #E98C26',
  borderRadius: '8px',
  fontSize: '1.3rem',
  fontWeight: 800,
  color: '#E98C26',
  fontFamily: 'monospace'
};
